import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  clearDocumentUploadError,
  updatePermitApplicationData,
} from '../../store/slices/apply/applySlice';
import {
  selectApplicationIsPaymentRequired,
  selectDocumentUploadErrors,
  selectDocumentUploadStarted,
  selectDocumentUploadWorking,
  selectPermitApplicationData,
} from '../../store/slices/apply/applySelectors';
import {
  selectConfigExpressApplyRentalStructureRequired,
  selectConfigExpressLocalContactVerbiage,
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressParcelVerbiage,
  selectConfigExpressApply,
} from '../../store/slices/config/configSelectors';
import { uploadApplicationDocuments } from '../../store/slices/apply/applyThunks';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import {
  payNothing,
  PayNothingOutcome,
} from '../../store/slices/payment/paymentThunk';
import { selectCurrentUserEmail } from '../../store/slices/auth/authSelectors';
import { FLOWS_ENUM } from '../../common/enums';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import { selectCurrentStep } from '../../store/slices/flow/flowSelectors';
import { ConfirmationPage } from 'src/components/pages/ConfirmationPage';
import { RegistrationFlow } from 'src/types/configuration';
import { useAppDispatch } from 'src/store/store';
import { ApplicationData } from 'src/types/flow';

const ApplyConfirmation = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();
  const configRenew = useSelector(selectConfigExpressApply);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingConfirmation, setIsProcessingConfirmation] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedPermitApplicationData = useSelector(
    selectPermitApplicationData
  );

  const selectedDocumentUploadStarted = useSelector(
    selectDocumentUploadStarted
  );
  const selectedDocumentUploadWorking = useSelector(
    selectDocumentUploadWorking
  );
  const selectedEmailAddress = useSelector(selectCurrentUserEmail);
  const selectedPaymentRequired = useSelector(
    selectApplicationIsPaymentRequired
  );
  const selectedRentalStructureRequiredStatus = useSelector(
    selectConfigExpressApplyRentalStructureRequired
  );

  const localContactVerbiage = useSelector(
    selectConfigExpressLocalContactVerbiage
  );
  const permitTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const parcelVerbiage = useSelector(selectConfigExpressParcelVerbiage);
  const uploadDocumentsError = useSelector(selectDocumentUploadErrors);

  useEffect(() => {
    if (uploadDocumentsError?.length > 0) {
      setErrorMessage(uploadDocumentsError.join('\n'));
    }
  }, [uploadDocumentsError]);

  const finalizeOnNext = (
    wasSuccessfull: boolean,
    requiresPayment: boolean
  ) => {
    if (wasSuccessfull) {
      if (requiresPayment) {
        setIsUploading(false);
        setIsProcessingConfirmation(true);
        dispatch(
          setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
        );
        dispatch(setCurrentStep(selectedCurrentStep + 1));
        placeNavigate('/apply-payment');
      } else {
        dispatch(payNothing(selectedEmailAddress, onPayNothingCompleted));
      }
    } else {
      setIsUploading(false);
      setIsProcessingConfirmation(false);
    }
  };

  const onPayNothingCompleted = (result?: PayNothingOutcome) => {
    setIsUploading(false);
    setIsProcessingConfirmation(true);
    if (result?.success) {
      placeNavigate('/apply-success');
    } else {
      setErrorMessage(
        'Unable to successfully finalize the new license creation.'
      );
    }
  };

  const onGoBack = () => {
    dispatch(clearDocumentUploadError());
    return true;
  };

  const flowError = useMemo(() => {
    if (errorMessage) {
      return errorMessage;
    }
    return '';
  }, [errorMessage]);

  const onConfirmation = (data: ApplicationData) => {
    dispatch(updatePermitApplicationData(data));

    if (!selectedDocumentUploadStarted && !selectedDocumentUploadWorking) {
      dispatch(uploadApplicationDocuments(finalizeOnNext));
    }
  };
  const instructionsText = useMemo(() => {
    return selectedPaymentRequired
      ? 'Please confirm the information below to proceed to the payment step.'
      : 'Please confirm the following information and sign below.';
  }, [selectedPaymentRequired]);

  return (
    <ConfirmationPage
      config={configRenew as RegistrationFlow}
      onGoBack={onGoBack}
      data={selectedPermitApplicationData}
      documentUploadErrors={uploadDocumentsError}
      flow={FLOWS_ENUM.APPLY_LICENSE}
      flowError={flowError}
      instructionsText={instructionsText}
      loading={isProcessingConfirmation || isUploading}
      localContactVerbiage={localContactVerbiage}
      onConfirmation={onConfirmation}
      parcelVerbiage={parcelVerbiage}
      permitTypeVerbiage={permitTypeVerbiage}
      rentalStructureRequiredStatus={selectedRentalStructureRequiredStatus}
    />
  );
};

export default ApplyConfirmation;
